import React, { useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  Divider,
  Avatar,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PageContainer from "../../components/layout/PageContainer";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useGetUserByIdQuery } from "../../store/apis/usersApi";
import {
  useGetCompaniesByUserIdQuery,
  useRemoveUsersFromCompanyMutation,
} from "../../store/apis/companyApi";
import dayjs from "dayjs";
import EditIcon from "@mui/icons-material/Edit";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import UserLoginHistory from "../../components/users/UserLoginHistory";

// Company ids to unlink from the user; "all" unlinks every company.
type UnlinkTarget = { companyId: string; name: string } | "all" | null;

const ViewUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch user data
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
  } = useGetUserByIdQuery(id || "", { skip: !id });
  const user = userData?.data;

  // Get companies the user is attached to
  const { data: companiesData, isLoading: isLoadingCompanies } =
    useGetCompaniesByUserIdQuery(id || "", { skip: !id });
  const userCompanies = companiesData?.data || [];

  const [unlinkTarget, setUnlinkTarget] = useState<UnlinkTarget>(null);
  const [removeUsersFromCompany] = useRemoveUsersFromCompanyMutation();
  const [isUnlinking, setIsUnlinking] = useState(false);

  const handleConfirmUnlink = async () => {
    if (!id || !unlinkTarget) return;
    const companyIds =
      unlinkTarget === "all"
        ? userCompanies.map((company) => company.id)
        : [unlinkTarget.companyId];

    setIsUnlinking(true);
    const results = await Promise.allSettled(
      companyIds.map((companyId) =>
        removeUsersFromCompany({ id: companyId, userIds: [id] }).unwrap()
      )
    );
    setIsUnlinking(false);
    setUnlinkTarget(null);

    const failed = results.filter((r) => r.status === "rejected").length;
    dispatch(
      setToastMessage(
        failed === 0
          ? {
              message: t(
                "users.profile.unlinkSuccess",
                "Company access removed"
              ),
              severity: "success",
            }
          : {
              message: t("users.profile.unlinkError", {
                defaultValue: "Could not unlink {{count}} companies",
                count: failed,
              }),
              severity: "error",
            }
      )
    );
  };

  // Header with back button - used in both success and error states
  const pageHeader = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 4,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Button
          startIcon={<KeyboardBackspaceIcon />}
          onClick={() => navigate(-1)}
          sx={{ alignSelf: "flex-start", mb: 2 }}
        >
          {t("common.back")}
        </Button>
        <Typography variant="h4" fontWeight="bold">
          {t("users.profile.title")}
        </Typography>
      </Box>
      {user && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/users/${id}/edit`)}
        >
          {t("users.edit")}
        </Button>
      )}
    </Box>
  );

  // Main content area
  const renderContent = () => {
    if (isLoadingUser || isLoadingCompanies) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <OrbLoader
            size="large"
            label={t("common.loading", { defaultValue: "Cargando…" })}
          />
        </Box>
      );
    }

    // Only show error if there's an actual API error
    if (userError) {
      return (
        <Paper
          sx={{
            p: 4,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            {t("users.errors.fetchError", { error: "API Error" })}
          </Typography>
          <Typography color={theme.palette.text.secondary}>
            {t("common.errorLoading")}
          </Typography>
        </Paper>
      );
    }

    if (!user) {
      return (
        <Paper
          sx={{
            p: 4,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="warning.main" gutterBottom>
            {t("users.notFoundTitle")}
          </Typography>
          <Typography color={theme.palette.text.secondary}>
            {t("users.notFoundMessage")}
          </Typography>
        </Paper>
      );
    }

    return (
      <>
        {/* User Profile Card */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Avatar
              alt={`${user.firstName} ${user.lastName}`}
              sx={{ width: 80, height: 80, mr: 3 }}
            >
              {user.firstName?.[0]}
              {user.lastName?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body1" color={theme.palette.text.secondary}>
                {user.role}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={t(`userStatus.${user.active ? "active" : "inactive"}`)}
                  sx={{
                    backgroundColor: user.active
                      ? theme.palette.success.main
                      : theme.palette.neutral["400"],
                    color: "white",
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* User Details */}
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {t("users.profile.yourData")}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.email")}
                </Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.phone")}
                </Typography>
                <Typography variant="body1">{user.phone || "-"}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.createdAt")}
                </Typography>
                <Typography variant="body1">
                  {user.createdAt && dayjs(user.createdAt).isValid()
                    ? dayjs(user.createdAt).format("MMM D, YYYY")
                    : "-"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.lastUpdate")}
                </Typography>
                <Typography variant="body1">
                  {user.updatedAt && dayjs(user.updatedAt).isValid()
                    ? dayjs(user.updatedAt).format("MMM D, YYYY")
                    : "-"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {t("users.firebaseUid")}
                </Typography>
                <Typography variant="body1">{user.firebaseUid}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Associated Companies */}
        <Paper
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {t("company.associatedCompanies")}
            </Typography>
            {isAdmin && userCompanies.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={() => setUnlinkTarget("all")}
              >
                {t("users.profile.unlinkAll", "Unlink all")}
              </Button>
            )}
          </Box>

          {userCompanies.length > 0 ? (
            <Grid container spacing={2}>
              {userCompanies.map((company) => (
                <Grid item xs={12} sm={6} md={4} key={company.id}>
                  <Paper
                    sx={{
                      position: "relative",
                      p: 2,
                      pr: isAdmin ? 6 : 2,
                      borderRadius: 2,
                      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                    onClick={() => navigate(`/companies/${company.id}`)}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {company.nameCommercial || company.nameFiscal}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}
                    >
                      {company.email || "-"}
                    </Typography>
                    {isAdmin && (
                      <Tooltip title={t("users.profile.unlink", "Unlink")}>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={t("users.profile.unlink", "Unlink")}
                          onClick={(e) => {
                            e.stopPropagation();
                            setUnlinkTarget({
                              companyId: company.id,
                              name:
                                company.nameCommercial || company.nameFiscal,
                            });
                          }}
                          sx={{ position: "absolute", top: 8, right: 8 }}
                        >
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" color={theme.palette.text.secondary}>
                {t("company.noAssociatedCompanies")}
              </Typography>
            </Box>
          )}
        </Paper>

        {id && <UserLoginHistory userId={id} />}
      </>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          {pageHeader}
          {renderContent()}
        </PageContainer>
      </Box>

      <ConfirmationModal
        open={!!unlinkTarget}
        title={
          unlinkTarget === "all"
            ? t("users.profile.unlinkAllTitle", "Unlink all companies")
            : t("users.profile.unlinkTitle", "Unlink company")
        }
        message={
          unlinkTarget === "all"
            ? t("users.profile.unlinkAllMessage", {
                defaultValue:
                  "This user will lose access to the campaigns of all {{count}} companies. Continue?",
                count: userCompanies.length,
              })
            : t("users.profile.unlinkMessage", {
                defaultValue:
                  "This user will lose access to the campaigns of {{name}}. Continue?",
                name: unlinkTarget?.name ?? "",
              })
        }
        confirmLabel={t("users.profile.unlink", "Unlink")}
        onConfirm={handleConfirmUnlink}
        onCancel={() => setUnlinkTarget(null)}
        isLoading={isUnlinking}
      />
    </Box>
  );
};

export default ViewUser;
